import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams, useSearch } from '@tanstack/react-router';
import { ShoppingBag } from 'lucide-react';
import { useState } from 'react';

import { kinds } from '@/app/site/scope';
import { useSiteScope } from '@/app/site/useSiteScope';
import { CatalogHead } from '@/features/catalog/Catalog';
import catalog from '@/features/catalog/Catalog.module.scss';
import { contentTypesGet } from '@/shared/api/generated/content/content';
import { shopEnable } from '@/shared/api/generated/shop/shop';
import { ApiError } from '@/shared/api/http';
import { Button } from '@/shared/ui/Button';
import { Counter } from '@/shared/ui/Counter';
import { EmptyState } from '@/shared/ui/EmptyState';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/shared/ui/Tabs';
import { toast } from '@/shared/ui/Toast';

import { Submissions } from '../FormsPage/SubmissionsList';
import { DeliveryTab } from './DeliveryTab';
import { GiftCardsTab } from './GiftCardsTab';
import { PromosTab } from './PromosTab';
import { ShopSettings } from './ShopSettings';
import type { ShopTab } from './tabs';

/**
 * «Магазин» (фаза 9, docs/research/11-shop.md в faceta-back): заказы и
 * настройки оплаты. Товары и заказы — обычные коллекции окружения; раздел
 * собирает то, что нужно каждый день: новые заказы и как их оплачивают.
 * Пока пак не включён, раздел предлагает включить.
 */
export function ShopPage() {
  const { siteId, env } = useParams({ from: '/authed/sites/$siteId/$env/shop' });
  const { tab = 'orders' } = useSearch({ from: '/authed/sites/$siteId/$env/shop' });
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const scope = useSiteScope();
  const shaper = scope?.can('site.shape') ?? false;
  const [enabling, setEnabling] = useState(false);
  const shop = scope?.shop;
  const ordersId = shop?.orders?.id;

  const orders = useQuery({
    queryKey: ['content-type', ordersId],
    enabled: ordersId !== undefined,
    queryFn: async () => {
      const response = await contentTypesGet(ordersId ?? '');

      if (response.status !== 200) {
        throw new Error('Неожиданный ответ на запрос заказов');
      }

      return response.data;
    },
  });

  const setTab = (next: string) => {
    void navigate({
      to: '/sites/$siteId/$env/shop',
      params: { siteId, env },
      search: next === 'orders' ? {} : { tab: next as ShopTab },
      replace: true,
    });
  };

  const enable = () => {
    setEnabling(true);

    shopEnable(siteId, kinds[env], {})
      .then(() => {
        toast.success('Магазин включён');
        void queryClient.invalidateQueries({ queryKey: ['shop', siteId] });
        void queryClient.invalidateQueries({ queryKey: ['content-types', siteId] });
      })
      .catch((cause: unknown) => {
        toast.error(cause instanceof ApiError ? cause.detail : 'Не удалось включить магазин');
      })
      .finally(() => {
        setEnabling(false);
      });
  };

  if (scope === null || scope.loading) {
    return null;
  }

  if (shop === undefined) {
    return (
      <div className={catalog['page']}>
        <CatalogHead
          title="Магазин"
          subtitle="Оформление заказа, остатки товаров и онлайн-оплата — сайт отдаёт корзину, остальное делает Faceta."
        />
        <EmptyState
          icon={<ShoppingBag />}
          title="Магазин не включён"
          description={`Включите — и заведутся «Категории», «Товары» и «Заказы» (если их ещё нет). Заказ, резерв товара и платёж — одно действие сервера; оплаченный заказ сам переходит в «оплачен». На ${
            env === 'dev'
              ? 'Development оплата тестовая — без ЮKassa и настоящих денег'
              : 'Production оплата — через ЮKassa: ключи вписываются в настройках магазина'
          }.`}
          action={
            shaper ? (
              <Button loading={enabling} onClick={enable}>
                Включить магазин
              </Button>
            ) : undefined
          }
        />
      </div>
    );
  }

  const type = orders.data?.type;

  return (
    <div className={catalog['page']}>
      <CatalogHead
        title="Магазин"
        subtitle={
          <>
            {shop.provider === 'test' ? 'Тестовая оплата' : 'ЮKassa'}
            {shop.provider === 'yookassa' && !shop.yookassa_secret_set && ' — ключи не заданы'}
            {' · товары — '}
            {shop.products !== undefined && (
              <Link
                to="/sites/$siteId/$env/models/$typeId"
                params={{ siteId, env, typeId: shop.products.id }}
              >
                {shop.products.name}
              </Link>
            )}
            {` · резерв неоплаченного — ${String(shop.reserve_minutes ?? 60)} мин`}
          </>
        }
      />

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="orders" active={tab === 'orders'}>
            Заказы <Counter value={type?.unread_count ?? 0} tone="accent" />
          </TabsTrigger>
          {shaper && (
            <TabsTrigger value="promos" active={tab === 'promos'}>
              Промокоды
            </TabsTrigger>
          )}
          {shaper && (
            <TabsTrigger value="gift-cards" active={tab === 'gift-cards'}>
              Сертификаты
            </TabsTrigger>
          )}
          {shaper && (
            <TabsTrigger value="delivery" active={tab === 'delivery'}>
              Доставка
            </TabsTrigger>
          )}
          {shaper && (
            <TabsTrigger value="settings" active={tab === 'settings'}>
              Настройки
            </TabsTrigger>
          )}
        </TabsList>

        <TabsContent value="orders">
          {type !== undefined && (
            <Submissions
              key={`${type.id}:${env}`}
              siteId={siteId}
              env={env}
              type={type}
              fields={orders.data?.fields ?? []}
              canDelete={false}
            />
          )}
        </TabsContent>

        {shaper && (
          <TabsContent value="promos">
            <PromosTab siteId={siteId} env={env} shop={shop} />
          </TabsContent>
        )}

        {shaper && (
          <TabsContent value="gift-cards">
            <GiftCardsTab siteId={siteId} env={env} shop={shop} />
          </TabsContent>
        )}

        {shaper && (
          <TabsContent value="delivery">
            <DeliveryTab siteId={siteId} env={env} />
          </TabsContent>
        )}

        {shaper && (
          <TabsContent value="settings">
            <ShopSettings
              key={shop.etag}
              siteId={siteId}
              env={env}
              shop={shop}
              onSaved={() => {
                void queryClient.invalidateQueries({ queryKey: ['shop', siteId, env] });
              }}
            />
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}
